/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import type * as Models from '../models'
import {
  GraphQueryMgr,
  PrivilegePageQueryReq,
  PrivilegePageQueryReq_PrivilegeViewType
} from '@src/endpoints/services/graphQueryMgr.service'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import type { State } from '@src/redux/configureStore'
import { parsePrivilegesSummary } from '../helpers'
import uuidv4 from 'uuid/v4'
import { toastError } from '@src/utils/toast'
import { isEmpty } from 'lodash'

export const initialState = {
  sessionID: null as string,
  privilegesList: [] as Models.PrivilegeSummaryItem[],
  has_next: false,
  has_prev: false,
  errors: null as any,
  isPending: false,
  selectedView: PrivilegePageQueryReq_PrivilegeViewType.USED
}

export type InitialState = typeof initialState

export interface GetDataPrivilegesPayload {
  privilegesList: Models.PrivilegeSummaryItem[]
  has_next: boolean
  has_prev: boolean
  isPending: boolean
}

const privilegesSummary = createSlice({
  name: 'privilegesSummary',
  initialState,
  reducers: {
    changeState: (state, action: PayloadAction<Partial<InitialState>>) =>
      ({ ...state, ...action.payload } as InitialState),
    pending: (state, action: PayloadAction<{ isPending?: boolean }>) => {
      state.isPending = action.payload.isPending
    },
    changeView: (
      state,
      action: PayloadAction<{ view: PrivilegePageQueryReq_PrivilegeViewType }>
    ) => {
      state.selectedView = action.payload.view
    },
    loadedPrivileges: (
      state,
      action: PayloadAction<GetDataPrivilegesPayload>
    ) => {
      state.has_next = action.payload.has_next
      state.has_prev = action.payload.has_prev
      state.privilegesList = action.payload.privilegesList
      state.isPending = action.payload.isPending
    },
    setSessionId: (state, action: PayloadAction<{ id: string }>) => {
      state.sessionID = action.payload.id
    },
    dispose: state => initialState
  }
})

export const createSession =
  (callback: (id: string) => void) =>
  (dispatch: any, getStore: () => State) => {
    dispatch(closeSession())
    const session_id = uuidv4()
    dispatch(privilegesSummary.actions.setSessionId({ id: session_id }))
    callback(session_id)
  }

export const closeSession = () => (dispatch: any, getStore: () => State) => {
  const { privilegesSummary: privilegesSummaryState } = getStore()
  const { sessionID } = privilegesSummaryState
  dispatch(privilegesSummary.actions.setSessionId({ id: null }))
  dispatch(releaseSession(sessionID))
}

export const loadPrivileges =
  (
    params: PrivilegePageQueryReq,
    view_type: PrivilegePageQueryReq_PrivilegeViewType
  ) =>
  (dispatch: any, getStore: () => State) => {
    const { filter, auth } = getStore()
    const { tenant = {} } = auth
    const { account_info = [] } = tenant
    dispatch(
      privilegesSummary.actions.changeState({
        isPending: true
      })
    )
    GraphQueryMgr.PrivilegePageQuery({
      params: paramsWithFilter(params, filter),
      queryVars: {
        view_type
      }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              privilegesSummary.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { privilege_summary = [], has_next, has_prev } = response || {}
        const privilegesList: Models.PrivilegeSummaryItem[] =
          parsePrivilegesSummary(privilege_summary, account_info)
        dispatch(
          privilegesSummary.actions.loadedPrivileges({
            isPending: false,
            privilegesList,
            has_next,
            has_prev
          })
        )
      })
      .catch((res: Response) => {
        dispatch(
          privilegesSummary.actions.changeState({
            isPending: false
          })
        )
      })
  }

export function releaseSession(sessionID: string, callback?: () => void) {
  return (dispatch: any, getStore: () => State) => {
    if (!isEmpty(sessionID)) {
      GraphQueryMgr.CloseSearchSession({
        params: {
          req_hdr: {
            tid: uuidv4()
          },
          session_id: sessionID
        },
        options: { signal: null }
      }).then(res => {
        if (!isEmpty(res.error)) {
          toastError(res.error)
        }
        if (callback) {
          callback()
        }
      })
    } else if (callback) {
      callback()
    }
  }
}

export const { reducer, actions, caseReducers } = privilegesSummary

export default privilegesSummary
